import { Monitor, MonitorOff, MonitorCog } from 'lucide-react'
import { Dropdown, IconButton, Tooltip } from '@/components/ui'
import { PERMISSIONS } from '@/features/authorization/permissions'
import { useSessionStore } from '@/stores'
import { useMeetingScreenCapture } from '../hooks'

/**
 * Whether ONE meeting's shared screen is recorded.
 *
 * IT WORKS ON A MEETING THAT DOES NOT EXIST IN THE DATABASE YET, and that is
 * the whole reason it lives on the "Up next" card rather than on a meeting
 * page. The scheduler writes a meeting's row at CLAIM time -- five minutes
 * before it starts -- so by the time there is a page to put this on, the
 * decision has already been made for you. The id shown here is
 * `uuid5(namespace, canonical_key)`, which the calendar entry already has.
 *
 * THREE STATES, NOT A TOGGLE. "Follow the workspace", "always", "never" are
 * different answers, and a two-state control cannot express the third: an
 * admin who later turns the workspace default on would silently reach a
 * meeting somebody had deliberately turned off. `source` tells them apart --
 * it is the server's answer, because comparing the value to the default cannot
 * distinguish "set to the same thing" from "not set".
 */
export function ScreenCaptureControl({
  meetingId,
  title,
}: {
  meetingId: string
  title: string
}) {
  const permissions = useSessionStore((s) => s.permissions)
  // Fails closed: null until the session is known, so this is hidden rather
  // than flashed to somebody who may not use it.
  const allowed = permissions?.includes(PERMISSIONS.meetingScreenCapture) ?? false
  const { query, save } = useMeetingScreenCapture(meetingId, allowed)

  if (!allowed) return null

  const state = query.data
  const overridden = state?.source === 'meeting'
  const capturing = state?.screenCapture ?? false

  const icon = !state ? (
    <MonitorCog />
  ) : capturing ? (
    <Monitor className="text-brand" />
  ) : overridden ? (
    <MonitorOff className="text-fg-subtle" />
  ) : (
    <MonitorCog className="text-fg-subtle" />
  )

  const label = !state
    ? 'Screen capture'
    : overridden
      ? capturing
        ? 'Screen capture on for this meeting'
        : 'Screen capture off for this meeting'
      : `Following the workspace default (${capturing ? 'on' : 'off'})`

  return (
    <Dropdown
      align="end"
      heading="Record the shared screen"
      trigger={({ toggle }) => (
        <Tooltip content={label}>
          <IconButton
            size="sm"
            label={`${label} — ${title}`}
            onClick={toggle}
            disabled={query.isPending || save.isPending}
            icon={icon}
          />
        </Tooltip>
      )}
      items={[
        {
          id: 'inherit',
          label: `Follow the workspace (${state?.workspaceDefault ? 'on' : 'off'})`,
          selected: !overridden,
          onSelect: () => save.mutate(null),
        },
        {
          id: 'on',
          label: 'Always capture this meeting',
          selected: overridden && capturing,
          onSelect: () => save.mutate(true),
        },
        {
          id: 'off',
          label: 'Never capture this meeting',
          selected: overridden && !capturing,
          onSelect: () => save.mutate(false),
        },
      ]}
    />
  )
}
